import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Zap, Target, Mail, Send, Phone, Search, Calendar,
  CheckCircle2, Lock, ChevronRight, Loader2, X, Sparkles
} from 'lucide-react';
import api from '../../lib/api';
import { useBillingCurrency } from '../../hooks/useBillingCurrency';
import { toast } from 'sonner';

const CAPABILITY_ICONS = {
  lead_scoring: Target,
  email_drafting: Mail,
  lead_enrichment: Search,
  email_sending: Send,
  voice_calls: Phone,
  meeting_scheduling: Calendar,
};

function SetupInline({ type, onSave, saving }) {
  const [value, setValue] = useState('');
  const isEmail = type === 'sender_email';

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSave(type, value.trim()); }}
      className="flex items-center gap-2 mt-2 animate-[fadeIn_0.2s_ease-out]"
    >
      <Input
        type={isEmail ? 'email' : 'tel'}
        placeholder={isEmail ? 'you@company.com' : '+1 (555) 123-4567'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-8 text-sm flex-1"
        data-testid={`power-setup-input-${type}`}
        required
      />
      <Button
        type="submit"
        size="sm"
        disabled={!value.trim() || saving}
        className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-500 text-white"
        data-testid={`power-setup-save-${type}`}
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
      </Button>
    </form>
  );
}

export default function PowerUpBrainCard() {
  const billing = useBillingCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSetup, setExpandedSetup] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/power-status');
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSave = async (type, value) => {
    setSaving(true);
    try {
      const payload = type === 'sender_email'
        ? { sender_email: value }
        : { caller_phone: value };
      await api.post('/api/brain/power-setup', payload);
      toast.success(type === 'sender_email' ? 'Sender email saved!' : 'Phone number saved!');
      setExpandedSetup(null);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="power-up-brain-card">
        <CardContent className="py-5">
          <div className="h-32 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { power_level, capabilities, credit_balance, active_count, total_count } = data;
  const allActive = active_count === total_count;

  return (
    <Card
      className="relative overflow-hidden border-0 shadow-md"
      data-testid="power-up-brain-card"
    >
      {/* Subtle gradient accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400" />

      <CardContent className="pt-5 pb-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Zap className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
                Power Up Your Brain
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {allActive ? 'All systems go!' : 'Connect tools to unlock full power'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-slate-900 dark:text-white">{power_level}%</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium">Power</div>
          </div>
        </div>

        {/* Power bar */}
        <div className="mb-4">
          <Progress
            value={power_level}
            className="h-2 bg-slate-100 dark:bg-slate-800"
            data-testid="power-level-bar"
          />
        </div>

        {/* Capabilities grid */}
        <div className="space-y-1.5">
          {capabilities.map((cap) => {
            const Icon = CAPABILITY_ICONS[cap.id] || Zap;
            const isActive = cap.status === 'active';
            const needsSetup = cap.status === 'setup';
            const isExpanded = expandedSetup === cap.id;

            return (
              <div key={cap.id} data-testid={`power-capability-${cap.id}`}>
                <div className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  {/* Status icon */}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                    isActive
                      ? 'bg-emerald-100 dark:bg-emerald-900/30'
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {isActive ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <Lock className="h-3 w-3 text-slate-400" />
                    )}
                  </div>

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${
                      isActive ? 'text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'
                    }`}>
                      {cap.name}
                    </span>
                  </div>

                  {/* Action */}
                  {isActive && (
                    <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 border-0">
                      Active
                    </Badge>
                  )}
                  {needsSetup && cap.setup_type && cap.setup_type !== 'calendar' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400"
                      onClick={() => setExpandedSetup(isExpanded ? null : cap.id)}
                      data-testid={`power-setup-btn-${cap.id}`}
                    >
                      {isExpanded ? <X className="h-3 w-3" /> : 'Set up'}
                    </Button>
                  )}
                  {needsSetup && cap.setup_type === 'calendar' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400"
                      onClick={() => window.location.href = '/integrations'}
                      data-testid="power-setup-btn-calendar"
                    >
                      Connect
                    </Button>
                  )}
                </div>

                {/* Inline setup form */}
                {isExpanded && needsSetup && cap.setup_type !== 'calendar' && (
                  <div className="pl-10 pr-2 pb-2">
                    <p className="text-xs text-slate-400 mb-1">{cap.setup_hint}</p>
                    <SetupInline type={cap.setup_type} onSave={handleSave} saving={saving} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Credit balance footer */}
        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <span className="text-xs text-slate-400">Credit Balance</span>
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200" data-testid="power-credit-balance">
            {billing.format(credit_balance)}
          </span>
        </div>
      </CardContent>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Card>
  );
}
